import { z } from 'zod';
import { ThemePreference, UserRole } from '../enums';

/**
 * User DTO Schema
 */
export const UserSchema = z.object({
  id: z.string().uuid(),
  businessId: z.string().uuid(),
  email: z.string().email(),
  name: z.string().min(1).max(255),
  role: z.nativeEnum(UserRole),
  isActive: z.boolean().default(true),
  themePreference: z.nativeEnum(ThemePreference).default(ThemePreference.SYSTEM),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  lastLoginAt: z.string().datetime().nullable().optional(),
});
export type User = z.infer<typeof UserSchema>;

/**
 * Create User DTO Schema
 * Note: businessId accepts any non-empty string to support both UUID and slug-based IDs
 */
export const CreateUserSchema = z.object({
  businessId: z.string().min(1, 'Business ID is required'),
  email: z.string().email(),
  name: z.string().min(1).max(255),
  role: z.nativeEnum(UserRole).default(UserRole.BUSINESS_USER),
  password: z.string().min(8).max(128),
});

export type CreateUser = z.infer<typeof CreateUserSchema>;

/**
 * Update User DTO Schema
 */
export const UpdateUserSchema = z
  .object({
    name: z.string().min(1).max(255).optional(),
    role: z.nativeEnum(UserRole).optional(),
    isActive: z.boolean().optional(),
  })
  .refine((data) => Object.keys(data).length > 0, {
    message: 'At least one field must be provided for update',
  });
export type UpdateUser = z.infer<typeof UpdateUserSchema>;

/**
 * Update User Preferences DTO Schema (self-service)
 * Users can update their own preferences without admin rights
 */
export const UpdateUserPreferencesSchema = z.object({
  themePreference: z.nativeEnum(ThemePreference).optional(),
  name: z.string().min(1).max(255).optional(),
});
export type UpdateUserPreferences = z.infer<typeof UpdateUserPreferencesSchema>;

/**
 * User Response DTO (excludes sensitive data)
 */
export const UserResponseSchema = UserSchema.omit({
  // Exclude any sensitive fields if needed
});

export type UserResponse = z.infer<typeof UserResponseSchema>;

/**
 * Reset Password DTO Schema (admin only)
 * Used to reset a user's password by an administrator
 */
export const ResetPasswordSchema = z.object({
  newPassword: z.string().min(8, 'Password must be at least 8 characters').max(128),
});
export type ResetPassword = z.infer<typeof ResetPasswordSchema>;
